/**
 * MarkdownView — renders agent prose with GitHub-flavored markdown.
 *
 * Used by the agent drawer for answer + reasoning bubbles. The Sonnet
 * prompt is tuned to emit markdown tables and bullet lists, which look
 * terrible as plain text. Wrapping in react-markdown with remark-gfm
 * gives us tables, task lists, and strikethrough out of the box.
 *
 * Styling is intentionally compact — the bubbles are narrow and the prose
 * sits inside a chat row, not an article. We override default element
 * styles via the `components` map so headings/lists/tables fit our scale
 * without globally restyling the rest of the app.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/cn";

interface MarkdownViewProps {
  text: string;
  /** Compact tweak — tighter line height + smaller table cells. Used in
   *  chat bubbles where vertical space is at a premium. */
  compact?: boolean;
  className?: string;
}

export function MarkdownView({ text, compact = true, className }: MarkdownViewProps) {
  return (
    <div className={cn("prose-aurora", compact && "prose-aurora-compact", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Strip out everything that could escape the bubble (no images,
          // no raw HTML — react-markdown skips HTML by default which is
          // exactly what we want from an LLM-generated string).
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet underline-offset-2 hover:underline"
            >
              {children}
            </a>
          ),
          // Headings shrunk one level since the chat bubble is itself
          // a card — avoids the model's `## Section` tags rendering as
          // page-level titles.
          h1: ({ children }) => <div className="mt-2 text-[13px] font-semibold text-fg">{children}</div>,
          h2: ({ children }) => <div className="mt-2 text-[12.5px] font-semibold text-fg">{children}</div>,
          h3: ({ children }) => <div className="mt-1.5 text-[12px] font-semibold text-fg">{children}</div>,
          p:  ({ children }) => <p className="my-1.5">{children}</p>,
          ul: ({ children }) => <ul className="my-1.5 ml-4 list-disc space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="my-1.5 ml-4 list-decimal space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="leading-snug">{children}</li>,
          code: ({ children, className: cls }) => {
            // Inline code (no language class) renders as a chip; fenced
            // blocks (`language-foo`) render as a pre block.
            const isBlock = (cls || "").startsWith("language-");
            if (!isBlock) {
              return <code className="rounded bg-surface px-1 py-0.5 font-mono text-[10.5px] text-violet">{children}</code>;
            }
            return (
              <pre className="my-1.5 overflow-x-auto rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-[10.5px] text-fg-dim">
                <code>{children}</code>
              </pre>
            );
          },
          // Tables — wrap in overflow-x-auto so wide tables don't blow up
          // the bubble width on narrow drawers.
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto">
              <table className="min-w-full border-collapse text-[11px]">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="border-b border-line">{children}</thead>,
          th:    ({ children }) => <th className="px-2 py-1 text-left font-semibold text-fg">{children}</th>,
          td:    ({ children }) => <td className="border-t border-line/40 px-2 py-1 text-fg-dim">{children}</td>,
          // Blockquotes — used by the model for callouts ("⚠️ heads-up...")
          blockquote: ({ children }) => (
            <blockquote className="my-1.5 border-l-2 border-violet/40 pl-2 italic text-fg-dim">
              {children}
            </blockquote>
          ),
          // Horizontal rules — model uses `---` between sections.
          hr: () => <hr className="my-2 border-line/60" />,
          strong: ({ children }) => <strong className="font-semibold text-fg">{children}</strong>,
          em:     ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
